import React from 'react';
import { Link } from 'react-router-dom';
import PaintingHeader from './PaintingHeader';
import PaintingFooter from './PaintingFooter';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { 
  Phone, 
  MapPin, 
  Clock, 
  CheckCircle, 
  Star,
  Home as HomeIcon,
  Building2,
  Paintbrush,
  Droplets,
  Shield,
  Award,
  Users,
  ThumbsUp
} from 'lucide-react';

const Home = () => {
  const services = [
    {
      icon: <HomeIcon className="w-8 h-8" />,
      title: "Residential Interior",
      description: "Transform your home with expert interior painting. Perfect finishes for every room.",
      image: "https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=600&h=400&fit=crop"
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Residential Exterior", 
      description: "Protect and beautify your home's exterior with weather-resistant quality paint.",
      image: "https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=600&h=400&fit=crop"
    },
    {
      icon: <Paintbrush className="w-8 h-8" />,
      title: "Commercial Painting",
      description: "Professional painting services for offices, retail spaces, and commercial properties.",
      image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=600&h=400&fit=crop"
    },
    {
      icon: <Droplets className="w-8 h-8" />,
      title: "Specialty Services",
      description: "Cabinet refinishing, deck staining, pressure washing, and drywall repair.",
      image: "https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=600&h=400&fit=crop"
    }
  ];

  const features = [
    {
      icon: <Shield className="w-12 h-12 text-[#1E3A8A]" />,
      title: "Licensed & Insured",
      description: "Fully licensed, bonded, and insured for your peace of mind"
    },
    {
      icon: <Award className="w-12 h-12 text-[#1E3A8A]" />,
      title: "20+ Years Experience",
      description: "Decades of experience delivering quality painting services"
    },
    {
      icon: <Users className="w-12 h-12 text-[#1E3A8A]" />,
      title: "Professional Team",
      description: "Skilled craftsmen dedicated to perfection"
    },
    {
      icon: <ThumbsUp className="w-12 h-12 text-[#1E3A8A]" />,
      title: "100% Satisfaction",
      description: "We don't finish until you're completely satisfied"
    }
  ];

  const testimonials = [
    {
      name: "Maria Rodriguez",
      location: "Hollywood, FL",
      rating: 5,
      text: "D'este Painting transformed our home! Professional, punctual, and the quality is outstanding. Highly recommended!"
    },
    {
      name: "John Smith", 
      location: "Fort Lauderdale, FL",
      rating: 5,
      text: "Best painting company in South Florida! They painted our commercial office and it looks amazing. Great communication throughout."
    },
    {
      name: "Lisa Chen",
      location: "Pembroke Pines, FL",
      rating: 5,
      text: "Incredible attention to detail. They treated our home like it was their own. Will definitely use them again!"
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <PaintingHeader />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=1920')] bg-cover bg-center opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-semibold mb-6">
              🏆 Hollywood's Trusted Painting Experts Since 2004
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none mb-6">
              Professional Painting Services in
              <span className="block text-[#EA580C] mt-2">Hollywood, Florida</span>
            </h1>
            <p className="text-xl sm:text-2xl text-white/90 font-light mb-8 leading-relaxed">
              Transform your space with D'este Painting. Quality craftsmanship, honest pricing, and exceptional results guaranteed.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link to="/contact">
                <Button size="lg" className="w-full sm:w-auto bg-[#EA580C] hover:bg-[#C2410C] text-white text-lg px-8 py-6">
                  Get Free Quote
                </Button>
              </Link>
              <a href="tel:+19548172046">
                <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white text-[#1E3A8A] border-2 border-white hover:bg-white/90 text-lg px-8 py-6">
                  <Phone className="w-5 h-5 mr-2" />
                  (954) 817-2046
                </Button>
              </a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8 border-t border-white/20">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-6 h-6 text-[#EA580C]" />
                <span className="text-white/90">Free Estimates</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-6 h-6 text-[#EA580C]" />
                <span className="text-white/90">Licensed & Insured</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-6 h-6 text-[#EA580C]" />
                <span className="text-white/90">Same-Day Response</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact Bar */}
      <section className="bg-white border-b border-[#E2E8F0] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center text-[#64748B]">
              <MapPin className="w-5 h-5 mr-2 text-[#1E3A8A]" />
              <span className="font-medium">1841 Rodman St, Hollywood, FL 33020</span>
            </div>
            <div className="flex items-center text-[#64748B]">
              <Clock className="w-5 h-5 mr-2 text-[#1E3A8A]" />
              <span className="font-medium">Mon-Fri: 7AM-6PM | Sat: 8AM-4PM</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-[#F1F5F9] rounded-full text-sm font-bold text-[#1E3A8A] uppercase tracking-wider mb-4">
              Our Services
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A] mb-4">
              Complete Painting Solutions
            </h2>
            <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
              From residential homes to commercial properties, we deliver exceptional results every time.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service) => (
              <Card key={service.title} className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-[#1E3A8A]">
                <div className="aspect-video overflow-hidden rounded-t-lg">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="mb-4 text-[#1E3A8A]">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2">
                    {service.title}
                  </h3>
                  <p className="text-[#64748B] leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <Link to="/services" className="text-[#1E3A8A] font-semibold hover:text-[#EA580C] transition-colors">
                    Learn More →
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-white rounded-full text-sm font-bold text-[#1E3A8A] uppercase tracking-wider mb-4">
              Why Choose Us
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A] mb-4">
              Hollywood's #1 Painting Company
            </h2>
            <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
              We're not just painters—we're craftsmen who take pride in every project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <div key={feature.title} className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-[#F1F5F9] rounded-2xl mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-2">
                  {feature.title}
                </h3>
                <p className="text-[#64748B] leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-[#F1F5F9] rounded-full text-sm font-bold text-[#1E3A8A] uppercase tracking-wider mb-4">
              Testimonials
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A] mb-4">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.name} className="border-2">
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`star-${testimonial.name}-${i}`} className="w-5 h-5 fill-[#EA580C] text-[#EA580C]" />
                    ))}
                  </div>
                  <p className="text-[#64748B] leading-relaxed mb-6 italic">
                    "{testimonial.text}"
                  </p>
                  <div>
                    <p className="font-bold text-[#0F172A]">{testimonial.name}</p>
                    <p className="text-sm text-[#64748B]">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-6">
            Ready to Transform Your Space?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Get your free, no-obligation quote today. We'll respond within 24 hours!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="w-full sm:w-auto bg-[#EA580C] hover:bg-[#C2410C] text-white text-lg px-8 py-6">
                Request Free Quote
              </Button>
            </Link>
            <a href="tel:+19548172046">
              <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white text-[#1E3A8A] hover:bg-white/90 text-lg px-8 py-6">
                <Phone className="w-5 h-5 mr-2" />
                Call (954) 817-2046
              </Button>
            </a>
          </div>
        </div>
      </section>

      <PaintingFooter />
    </div>
  );
};

export default Home;
